import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { CodeView } from "@rakazo/chat-ui/code-view";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import type { Artifact, ArtifactVersion } from "@rakazo/contracts";
import { NativeSelect, NativeSelectOption, Spinner } from "@rakazo/ui-web";
import { Check, Copy, Download, Lock, Minus, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useId, useMemo, useState, useSyncExternalStore } from "react";
import {
  downloadArtifactBytes,
  fetchArtifactById,
  listArtifactVersions,
} from "../lib/artifact-open";
import {
  type ArtifactViewKind,
  artifactCodeLanguage,
  artifactTableDelimiter,
  artifactViewKind,
  canRenderArtifact,
  isMarkupArtifactView,
  isTextArtifactView,
  parseDelimited,
} from "../lib/artifact-panel";
import { authClient } from "../lib/auth";
import { formatRelativeTime } from "../lib/relative-time";
import { useObjectUrl } from "../lib/use-object-url";
import { errorText } from "../lib/user-error";
import { rpcMarkupClient } from "./preview-markup/markup-client";
import { PdfMarkupView } from "./preview-markup/PdfMarkupView";
import { SandboxedHtmlViewer } from "./SandboxedHtmlViewer";

/** Which tab of the panel is shown: the rendered view or the raw source. */
export type ArtifactPanelTab = "preview" | "source";

type ArtifactPanelViewProps = {
  /** Any version of the artifact; the newest version of its family is shown first. */
  artifactId: string;
  /** Known up front in the chat; the gallery's deep link learns them from the first load. */
  name?: string;
  mimeType?: string;
  size?: number;
  /** Extra controls at the end of the toolbar (the gallery's maximize button). */
  actions?: ReactNode;
  /** Told the metadata of every version that loads (the gallery shows name and description). */
  onLoaded?: (artifact: Artifact) => void;
};

type LoadState =
  | { status: "loading" }
  | { status: "error"; reason: "not-utf8" }
  | { status: "error"; reason: "load"; error: unknown }
  | { status: "download-only"; name: string; mimeType: string; size: number }
  | {
      status: "ready";
      name: string;
      mimeType: string;
      size: number;
      bytes: Uint8Array;
      /** Decoded UTF-8 for text views; undefined for binary views. */
      text?: string;
    };

const toolbarButton =
  "inline-flex h-8 min-w-8 shrink-0 items-center justify-center gap-1.5 rounded-lg px-2 text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring disabled:opacity-50";

function subscribeTheme(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return () => observer.disconnect();
}

function readTheme(): "light" | "dark" {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

/**
 * Body of the artifact panel (PIV-36): tabs Preview/Source, version picker, copy,
 * download and the view that fits the type. The chat pane and the artifact
 * gallery both use it, so an artifact looks the same wherever it opens. PDF and Office
 * documents open in the markup view (RAK-168), drawn by pdf.js on canvas, without a source tab.
 */
export function ArtifactPanelView({
  artifactId,
  name = "",
  mimeType,
  size,
  actions,
  onLoaded,
}: ArtifactPanelViewProps) {
  const { t } = useLingui();
  const tabsId = useId();
  const colorScheme = useSyncExternalStore(subscribeTheme, readTheme, () => "light" as const);
  const [versions, setVersions] = useState<ArtifactVersion[] | null>(null);
  const [versionsFailed, setVersionsFailed] = useState(false);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [tab, setTab] = useState<ArtifactPanelTab>("preview");
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setVersions(null);
    setVersionsFailed(false);
    setPickedId(null);
    setTab("preview");
    listArtifactVersions(artifactId)
      .then((list) => {
        if (!cancelled) setVersions(list);
      })
      .catch(() => {
        if (!cancelled) setVersionsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [artifactId]);

  // Newest version first; without a version list the opened artifact itself.
  const selectedId =
    pickedId ?? (versions ? (versions[0]?.id ?? artifactId) : versionsFailed ? artifactId : null);
  const selectedVersion = versions?.find((entry) => entry.id === selectedId) ?? null;
  const knownName = selectedVersion?.name ?? name;
  const kind = artifactViewKind(knownName, mimeType ?? "");
  // Kind of the opened artifact as the caller knows it; decides whether to fetch at all.
  const openedKind = artifactViewKind(name, mimeType ?? "");

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setState({ status: "loading" });
    setActionError(null);
    // The opened version's size is known up front: too big means no fetch at all.
    if (
      selectedId === artifactId &&
      mimeType !== undefined &&
      size !== undefined &&
      !canRenderArtifact(openedKind, size)
    ) {
      setState({ status: "download-only", name, mimeType, size });
      return;
    }
    fetchArtifactById(selectedId)
      .then(({ artifact, bytes }) => {
        if (cancelled) return;
        onLoaded?.(artifact);
        const viewKind = artifactViewKind(artifact.name, artifact.mimeType);
        if (!canRenderArtifact(viewKind, artifact.size)) {
          setState({ status: "download-only", ...artifact });
          return;
        }
        if (!isTextArtifactView(viewKind)) {
          setState({ status: "ready", ...artifact, bytes });
          return;
        }
        try {
          const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
          setState({ status: "ready", ...artifact, bytes, text });
        } catch {
          setState({ status: "error", reason: "not-utf8" });
        }
      })
      .catch((error) => {
        if (!cancelled) setState({ status: "error", reason: "load", error });
      });
    return () => {
      cancelled = true;
    };
    // Translations stay out of the deps: messages are built at render time.
  }, [selectedId, artifactId, openedKind, size, name, mimeType, onLoaded]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const readyKind: ArtifactViewKind =
    state.status === "ready" ? artifactViewKind(state.name, state.mimeType) : kind;
  const hasSource = state.status === "ready" && state.text !== undefined;
  const activeTab: ArtifactPanelTab = hasSource ? tab : "preview";

  async function copyText() {
    if (state.status !== "ready" || state.text === undefined) return;
    setActionError(null);
    try {
      await navigator.clipboard.writeText(state.text);
      setCopied(true);
    } catch {
      setActionError(t`Could not copy. Select the text and copy it by hand.`);
    }
  }

  async function download() {
    setActionError(null);
    try {
      if (state.status === "ready") {
        downloadArtifactBytes(state.name, state.mimeType, state.bytes);
        return;
      }
      const id = selectedId ?? artifactId;
      const { artifact, bytes } = await fetchArtifactById(id);
      downloadArtifactBytes(artifact.name, artifact.mimeType, bytes);
    } catch {
      setActionError(t`Could not download ${knownName}. Try again.`);
    }
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-testid="artifact-panel"
      data-kind={readyKind}
    >
      <div className="flex min-h-11 shrink-0 flex-wrap items-center gap-0.5 border-b border-border px-2 py-1.5">
        {hasSource ? (
          <div
            role="tablist"
            aria-label={t`View`}
            className="flex items-center rounded-lg bg-muted/60 p-0.5"
          >
            {(["preview", "source"] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                id={`${tabsId}-${value}`}
                aria-selected={activeTab === value}
                aria-controls={`${tabsId}-panel`}
                tabIndex={activeTab === value ? 0 : -1}
                onClick={() => setTab(value)}
                onKeyDown={(event) => {
                  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                  event.preventDefault();
                  const next = value === "preview" ? "source" : "preview";
                  setTab(next);
                  document.getElementById(`${tabsId}-${next}`)?.focus();
                }}
                data-testid={`artifact-tab-${value}`}
                className="h-7 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground aria-selected:bg-background aria-selected:text-foreground aria-selected:shadow-sm focus-visible:outline-2 focus-visible:outline-ring"
              >
                {value === "preview" ? t`Preview` : t`Source`}
              </button>
            ))}
          </div>
        ) : null}
        <div className="min-w-0 flex-1" />
        {versions && versions.length > 1 && selectedId ? (
          <NativeSelect
            aria-label={t`Version`}
            data-testid="artifact-version"
            className="w-auto shrink-0 text-[13px]"
            value={selectedId}
            onChange={(event) => setPickedId(event.target.value)}
          >
            {versions.map((entry) => (
              <NativeSelectOption key={entry.id} value={entry.id}>
                {t`v${entry.version} · ${formatRelativeTime(entry.createdAt)}`}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        ) : selectedVersion ? (
          <span className="shrink-0 px-1.5 text-[13px] text-muted-foreground">
            {t`Version ${selectedVersion.version}`}
          </span>
        ) : null}
        {hasSource ? (
          <button
            type="button"
            className={toolbarButton}
            onClick={() => void copyText()}
            aria-label={copied ? t`Copied` : t`Copy`}
            title={t`Copy`}
            data-testid="artifact-copy"
          >
            {copied ? (
              <Check className="size-4" aria-hidden="true" />
            ) : (
              <Copy className="size-4" aria-hidden="true" />
            )}
          </button>
        ) : null}
        <button
          type="button"
          className={toolbarButton}
          onClick={() => void download()}
          aria-label={t`Download ${knownName}`}
          title={t`Download`}
          data-testid="artifact-download"
        >
          <Download className="size-4" aria-hidden="true" />
        </button>
        {actions}
      </div>

      {actionError ? (
        <div
          role="alert"
          className="shrink-0 border-b border-border bg-destructive/10 px-4 py-2 text-[13px] text-destructive"
        >
          {actionError}
        </div>
      ) : null}

      <div
        id={`${tabsId}-panel`}
        {...(hasSource ? { role: "tabpanel", "aria-labelledby": `${tabsId}-${activeTab}` } : {})}
        className="relative min-h-0 flex-1 overflow-hidden"
      >
        {state.status === "loading" ? (
          <div className="absolute inset-0 grid place-items-center text-muted-foreground">
            <Spinner aria-label={t`Loading…`} />
          </div>
        ) : state.status === "error" ? (
          <div className="m-5 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-[14px] text-destructive">
            {state.reason === "not-utf8"
              ? t`This file is not valid UTF-8 text.`
              : errorText(state.error, t`Could not load this artifact.`)}
          </div>
        ) : state.status === "download-only" ? (
          <DownloadOnly
            name={state.name}
            tooLarge={artifactViewKind(state.name, state.mimeType) !== "unknown"}
            onDownload={() => void download()}
          />
        ) : (
          <ArtifactBody
            artifactId={selectedId ?? artifactId}
            kind={readyKind}
            tab={activeTab}
            name={state.name}
            mimeType={state.mimeType}
            bytes={state.bytes}
            text={state.text}
            colorScheme={colorScheme}
            onDownload={() => void download()}
          />
        )}
      </div>
    </div>
  );
}

function ArtifactBody({
  artifactId,
  kind,
  tab,
  name,
  mimeType,
  bytes,
  text,
  colorScheme,
  onDownload,
}: {
  artifactId: string;
  kind: ArtifactViewKind;
  tab: ArtifactPanelTab;
  name: string;
  mimeType: string;
  bytes: Uint8Array;
  text?: string;
  colorScheme: "light" | "dark";
  onDownload: () => void;
}) {
  const sourceLabel = t`Source of ${name}`;
  if (text !== undefined && tab === "source") {
    if (kind === "html" || kind === "markdown") {
      return (
        <CodeView
          code={text}
          language={kind === "html" ? "markup" : "markdown"}
          colorScheme={colorScheme}
          label={sourceLabel}
        />
      );
    }
    return <RawText text={text} label={sourceLabel} />;
  }
  if (kind === "markdown" && text !== undefined) {
    return (
      <div className="h-full overflow-y-auto bg-background">
        <article className="mx-auto w-full max-w-[760px] px-6 py-8 text-[16px] leading-7 text-foreground sm:px-10">
          <ChatMarkdown>{text}</ChatMarkdown>
        </article>
      </div>
    );
  }
  if (kind === "code" && text !== undefined) {
    return (
      <CodeView
        code={text}
        language={artifactCodeLanguage(name, mimeType)}
        colorScheme={colorScheme}
        label={t`Code of ${name}`}
      />
    );
  }
  if (kind === "html" && text !== undefined) {
    return (
      <div className="relative h-full w-full bg-white">
        <SandboxedHtmlViewer html={text} title={name} />
        <div className="pointer-events-none absolute bottom-3 end-3 flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1.5 text-[11px] text-white">
          <Lock size={12} strokeWidth={2} aria-hidden="true" />
          <span>{t`Isolated preview — no access to your account`}</span>
        </div>
      </div>
    );
  }
  if (kind === "table" && text !== undefined) {
    const delimiter = artifactTableDelimiter(name, mimeType) ?? ",";
    return <TableView text={text} delimiter={delimiter} name={name} />;
  }
  if (isMarkupArtifactView(kind)) {
    return <ArtifactMarkup key={artifactId} artifactId={artifactId} name={name} />;
  }
  if (kind === "image") return <ImageView bytes={bytes} mimeType={mimeType} name={name} />;
  return <DownloadOnly name={name} tooLarge={false} onDownload={onDownload} />;
}

/**
 * PDF and Office files (RAK-168): the server's PDF rendering on canvas with the
 * markup tools. Office files are never rendered inline, only their PDF.
 */
function ArtifactMarkup({ artifactId, name }: { artifactId: string; name: string }) {
  const userId = authClient.useSession().data?.user.id ?? "";
  return (
    <PdfMarkupView artifactId={artifactId} name={name} userId={userId} client={rpcMarkupClient} />
  );
}

function RawText({ text, label }: { text: string; label: string }) {
  return (
    <section
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be keyboard-focusable (WCAG 2.1.1).
      tabIndex={0}
      className="h-full overflow-auto outline-none focus-visible:outline-2 focus-visible:outline-ring"
    >
      <pre
        data-testid="artifact-raw"
        className="m-0 w-max min-w-full whitespace-pre px-4 py-3 font-mono text-[13px] leading-[1.6] text-foreground"
      >
        {text}
      </pre>
    </section>
  );
}

/** At most this many rows are drawn; the rest stays in Source and the download. */
const TABLE_ROW_LIMIT = 2000;

function TableView({
  text,
  delimiter,
  name,
}: {
  text: string;
  delimiter: "," | "\t";
  name: string;
}) {
  const rows = useMemo(() => parseDelimited(text, delimiter), [text, delimiter]);
  const [head, ...body] = rows;
  if (!head) return <RawText text={text} label={t`Source of ${name}`} />;
  const shown = body.slice(0, TABLE_ROW_LIMIT);
  const columns = Math.max(head.length, ...shown.map((row) => row.length));
  const numeric = Array.from({ length: columns }, (_, column) =>
    shown.every((row) => {
      const cell = (row[column] ?? "").trim();
      return cell === "" || /^[-+]?\d[\d.,\s]*%?$/.test(cell);
    }),
  );
  return (
    // biome-ignore lint/a11y/noNoninteractiveTabindex: scrollable region must be reachable by keyboard.
    <div className="h-full overflow-auto" tabIndex={0} data-testid="artifact-table">
      <table className="min-w-full border-separate border-spacing-0 text-[13px]">
        <caption className="sr-only">{name}</caption>
        <thead>
          <tr>
            {Array.from({ length: columns }, (_, column) => (
              <th
                key={column}
                scope="col"
                className={`sticky top-0 z-[1] whitespace-nowrap border-b border-border bg-muted px-3 py-2 font-semibold text-foreground ${numeric[column] ? "text-end" : "text-start"}`}
              >
                {head[column] ?? ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((row, rowIndex) => (
            <tr key={rowIndex} className="even:bg-muted/40">
              {Array.from({ length: columns }, (_, column) => (
                <td
                  key={column}
                  className={`whitespace-nowrap border-b border-border/60 px-3 py-1.5 text-foreground ${numeric[column] ? "text-end tabular-nums" : "text-start"}`}
                >
                  {row[column] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {body.length > TABLE_ROW_LIMIT ? (
        <p className="px-3 py-2 text-[13px] text-muted-foreground">
          {t`Showing the first ${TABLE_ROW_LIMIT} of ${body.length} rows. Source and download contain all of them.`}
        </p>
      ) : null}
    </div>
  );
}

const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4];

function ImageView({
  bytes,
  mimeType,
  name,
}: {
  bytes: Uint8Array;
  mimeType: string;
  name: string;
}) {
  const url = useObjectUrl(bytes, mimeType);
  // null = fit to the pane; otherwise a fixed scale of the natural size.
  const [zoom, setZoom] = useState<number | null>(null);
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  if (!url) return null;
  const index = zoom === null ? -1 : ZOOM_STEPS.indexOf(zoom);
  const zoomIn = () => setZoom(zoom === null ? 1 : (ZOOM_STEPS[index + 1] ?? zoom));
  const zoomOut = () => setZoom(zoom === null ? 0.5 : (ZOOM_STEPS[index - 1] ?? zoom));
  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-auto bg-muted/40" data-testid="artifact-image">
        <div className="grid min-h-full min-w-full place-items-center p-4">
          {/* SVG too only ever goes through <img>: no scripts, no inline markup. */}
          <img
            src={url}
            alt={name}
            onLoad={(event) =>
              setNatural({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
            style={
              zoom !== null && natural
                ? { width: natural.width * zoom, height: natural.height * zoom, maxWidth: "none" }
                : undefined
            }
            className={`rounded-lg shadow-sm ${zoom === null ? "max-h-full max-w-full" : ""}`}
          />
        </div>
      </div>
      <fieldset
        aria-label={t`Zoom`}
        className="m-0 flex shrink-0 items-center justify-center gap-1 border-0 border-t border-border p-1.5"
      >
        <button
          type="button"
          className={toolbarButton}
          onClick={zoomOut}
          disabled={zoom !== null && index <= 0}
          aria-label={t`Zoom out`}
          title={t`Zoom out`}
        >
          <Minus className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`${toolbarButton} min-w-16 justify-center tabular-nums`}
          aria-pressed={zoom === null}
          onClick={() => setZoom(null)}
          title={t`Fit to pane`}
        >
          {zoom === null ? t`Fit` : `${Math.round(zoom * 100)} %`}
        </button>
        <button
          type="button"
          className={toolbarButton}
          onClick={zoomIn}
          disabled={zoom !== null && index >= ZOOM_STEPS.length - 1}
          aria-label={t`Zoom in`}
          title={t`Zoom in`}
        >
          <Plus className="size-4" aria-hidden="true" />
        </button>
      </fieldset>
    </div>
  );
}

function DownloadOnly({
  name,
  tooLarge,
  onDownload,
}: {
  name: string;
  tooLarge: boolean;
  onDownload: () => void;
}) {
  return (
    <div
      className="grid h-full place-items-center px-6 text-center"
      data-testid="artifact-download-only"
    >
      <div className="max-w-sm">
        <p className="text-[14px] text-muted-foreground">
          {tooLarge
            ? t`${name} is too large to show here. Download it to open it.`
            : t`This file type can't be shown here. Download it to open it.`}
        </p>
        <button
          type="button"
          onClick={onDownload}
          className="mt-3 inline-flex h-9 items-center gap-2 rounded-lg border border-border px-3 text-[14px] font-medium text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
        >
          <Download className="size-4" aria-hidden="true" />
          {t`Download`}
        </button>
      </div>
    </div>
  );
}
